import { useRef, useState } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import type { IAlert } from 'common/interfaces/IAlert'

import type { WarningProps } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/warning.types'
import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'

import {
  useDuplicateSeriesTemplateMutation,
  useSetSeriesTemplateActiveMutation,
} from '../../api/series.api'
import { isEmrAccessDenied, SERIES_ERROR_CODES, toSeriesApiError } from '../../api/series.transform'
import type { SeriesTemplate } from '../../api/series.types'
import { SERIES_PAGES_TEXT } from '../shared/seriesPagesText'

const TEXT = SERIES_PAGES_TEXT.templates
const TOASTS = SERIES_PAGES_TEXT.toasts
const NOTICE_TITLE = 'Series templates'
const NOT_FOUND = 404

export type TemplateBusyAction = 'duplicate' | 'toggle'

// The row and the action in flight, so only the clicked button spins.
export type TemplateBusy = { id: number; action: TemplateBusyAction }

type TemplateActions = {
  duplicate: (template: SeriesTemplate) => void
  toggleActive: (template: SeriesTemplate) => void
  busy: TemplateBusy | null
  warningProps: WarningProps
}

const errorNotice = (description: string): IAlert => ({
  title: NOTICE_TITLE,
  type: ALERT_CONSTANTS.ERROR,
  description,
})

const failureText = (error: unknown): string => {
  const failure = toSeriesApiError(error)
  if (failure.status === NOT_FOUND) return TOASTS.templateGone
  if (isEmrAccessDenied(failure)) return TOASTS.emrAccessDenied
  if (failure.code === SERIES_ERROR_CODES.EMR_UNAVAILABLE) return TOASTS.templateEmrNoAnswer
  return failure.message
}

export const useTemplateActions = (siteId: string): TemplateActions => {
  const [duplicateTemplate] = useDuplicateSeriesTemplateMutation()
  const [setActive] = useSetSeriesTemplateActiveMutation()
  const { setNotification } = useNotification()
  const [retiring, setRetiring] = useState<SeriesTemplate | null>(null)
  const [busy, setBusy] = useState<TemplateBusy | null>(null)
  const isSendingRef = useRef(false)

  const run = async (next: TemplateBusy, send: () => Promise<unknown>) => {
    if (isSendingRef.current) return
    isSendingRef.current = true
    setBusy(next)
    try {
      await send()
    } catch (error) {
      setNotification(errorNotice(failureText(error)))
    } finally {
      isSendingRef.current = false
      setBusy(null)
    }
  }

  const sendActive = (template: SeriesTemplate, active: boolean) =>
    run({ id: template.id, action: 'toggle' }, () =>
      setActive({ siteId, templateId: template.id, active }).unwrap(),
    )

  const duplicate = (template: SeriesTemplate) => {
    void run({ id: template.id, action: 'duplicate' }, () =>
      duplicateTemplate({
        siteId,
        templateId: template.id,
        name: TEXT.duplicateName(template.name),
      }).unwrap(),
    )
  }

  const toggleActive = (template: SeriesTemplate) => {
    if (template.active && template.facility === null) {
      setRetiring(template)
      return
    }
    void sendActive(template, !template.active)
  }

  const handleConfirm = () => {
    if (!retiring) return
    const target = retiring
    setRetiring(null)
    void sendActive(target, false)
  }

  return {
    duplicate,
    toggleActive,
    busy,
    warningProps: {
      open: retiring !== null,
      title: TEXT.retireTitle,
      warning: TEXT.retireWarning,
      okButtonText: INFO_CONSTANTS.YES,
      hasCancel: true,
      handleClose: () => setRetiring(null),
      handleConfirm,
    },
  }
}
